// src/pages/CheckoutReports.jsx
//
// Reports dashboard for the Student Checkout system. Read-only operational
// view for admins to see where students are stuck during checkout season.
//
// Layout: left sidebar with four report links, main panel renders the
// selected report. Each report shows three stacked cards — All Students,
// Upper School (9-12), Middle School (7-8) — so admins can compare
// scope-by-scope without navigating.
//
// Data: live Firestore subscription on /checkoutRecords (filtered to the
// current schoolYear, non-archived). Aggregations happen in the browser via
// useMemo. No new Cloud Functions needed.
//
// Exports:
//   - Each card has CSV + PDF download buttons (single scope, single report)
//   - Page header has "Download PDF (this report)" — all 3 scopes for the
//     currently-viewed report
//   - Sidebar bottom has "Download Everything" — all 4 reports × 3 scopes
//
// PDF style matches the AiChartGenerator/Peer Benchmarking exports: navy/gold
// cover, navy header bar, autoTable for the data tables, "Internal use only"
// footer on every non-cover page.
//
// Access: gated on /tools/checkout-reports via ToolRoute, toolId='checkout_reports'.

import React, { useEffect, useMemo, useState } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { db } from '../config/firebase';
import {
  collection,
  query,
  where,
  onSnapshot,
} from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import {
  CHECKOUT_SECTIONS,
  SECTION_STATUS,
  RECORD_STATUS,
  MS_GRADES,
  US_GRADES,
  isMsGrade,
  isUsGrade,
  getRelevantSectionIds,
  computeCompletionPercent,
  DEFAULT_SCHOOL_YEAR,
  KA_COLORS,
} from '../utils/checkoutHelpers';

// =============================================================================
// REPORT REGISTRY
// =============================================================================

const REPORTS = [
  {
    id: 'section_bottlenecks',
    label: 'Section Bottlenecks',
    description:
      'Pending vs signed counts for each section. Identifies which sections are slow.',
  },
  {
    id: 'completion_buckets',
    label: 'Completion Buckets',
    description:
      'How students are distributed across completion percentages.',
  },
  {
    id: 'stuck_students',
    label: 'Stuck Students',
    description:
      'Lowest-completion students — the "still needs the most" list.',
  },
  {
    id: 'almost_done',
    label: 'Almost Done',
    description:
      'Students at 80% or above but not yet 100%. The "easy wins" list.',
  },
];

const SCOPES = [
  { id: 'all', label: 'All Students', filter: () => true },
  { id: 'us', label: 'Upper School (9–12)', filter: (r) => isUsGrade(r.grade) },
  { id: 'ms', label: 'Middle School (7–8)', filter: (r) => isMsGrade(r.grade) },
];

// =============================================================================
// STYLES
// =============================================================================

const styles = {
  outer: {
    display: 'flex',
    minHeight: 'calc(100vh - 80px)',
    background: KA_COLORS.lightGray,
  },
  sidebar: {
    width: 240,
    background: '#fff',
    borderRight: `1px solid ${KA_COLORS.borderGray}`,
    padding: '20px 0',
    flexShrink: 0,
  },
  sidebarHeader: {
    padding: '0 20px 12px',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: '#888',
  },
  sidebarItem: {
    display: 'block',
    padding: '10px 20px',
    border: 'none',
    background: 'transparent',
    width: '100%',
    textAlign: 'left',
    cursor: 'pointer',
    fontSize: 14,
    color: KA_COLORS.navy,
    borderLeft: '3px solid transparent',
    transition: 'background 0.1s',
  },
  sidebarItemActive: {
    background: '#f4f5f8',
    borderLeftColor: KA_COLORS.red,
    fontWeight: 600,
  },
  main: {
    flex: 1,
    padding: '24px 32px',
    overflowX: 'auto',
  },
  pageHeader: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    marginBottom: 6,
    flexWrap: 'wrap',
    gap: 12,
  },
  pageTitle: {
    fontSize: 28,
    fontFamily: 'Georgia, serif',
    color: KA_COLORS.navy,
    margin: 0,
  },
  pageDescription: {
    color: '#666',
    fontSize: 13,
    marginTop: 4,
    marginBottom: 24,
  },
  printButton: {
    padding: '8px 16px',
    background: KA_COLORS.navy,
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  pdfAllButton: {
    padding: '8px 16px',
    background: KA_COLORS.navy,
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  pdfEverythingButton: {
    display: 'block',
    width: 'calc(100% - 28px)',
    margin: '20px 14px',
    padding: '10px 12px',
    background: KA_COLORS.gold,
    color: KA_COLORS.navy,
    border: 'none',
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
    letterSpacing: 0.3,
  },
  card: {
    background: '#fff',
    border: `1px solid ${KA_COLORS.borderGray}`,
    borderRadius: 8,
    marginBottom: 20,
    overflow: 'hidden',
    pageBreakInside: 'avoid',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 18px',
    borderBottom: `1px solid ${KA_COLORS.borderGray}`,
    background: '#f9f9fa',
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: KA_COLORS.navy,
    margin: 0,
  },
  cardCount: {
    fontSize: 12,
    color: '#666',
    fontWeight: 500,
  },
  cardActions: {
    display: 'flex',
    gap: 8,
  },
  csvButton: {
    padding: '5px 10px',
    background: '#fff',
    color: KA_COLORS.navy,
    border: `1px solid ${KA_COLORS.navy}`,
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
  pdfButton: {
    padding: '5px 10px',
    background: KA_COLORS.navy,
    color: '#fff',
    border: `1px solid ${KA_COLORS.navy}`,
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
  cardBody: {
    padding: 0,
  },
  emptyMessage: {
    padding: 24,
    textAlign: 'center',
    color: '#888',
    fontSize: 13,
    fontStyle: 'italic',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
  },
  th: {
    padding: '10px 14px',
    background: '#fafbfc',
    color: '#555',
    fontWeight: 600,
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    borderBottom: `1px solid ${KA_COLORS.borderGray}`,
    textAlign: 'left',
  },
  thRight: { textAlign: 'right' },
  td: {
    padding: '8px 14px',
    borderBottom: `1px solid #f0f0f0`,
    color: '#222',
  },
  tdMuted: { color: '#888' },
  tdRight: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  tdNumeric: { fontVariantNumeric: 'tabular-nums' },

  // Bar inside a table cell (for completion %, % signed, etc.)
  bar: {
    width: 80,
    height: 6,
    background: '#e8e8ec',
    borderRadius: 3,
    overflow: 'hidden',
    display: 'inline-block',
    position: 'relative',
    verticalAlign: 'middle',
    marginRight: 6,
  },
  barFill: (pct, color) => ({
    display: 'block',
    width: `${Math.max(0, Math.min(100, pct))}%`,
    height: '100%',
    background: color,
    borderRadius: 3,
  }),
  gradePill: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 700,
    color: '#fff',
  },
  loading: {
    textAlign: 'center',
    padding: 40,
    color: '#888',
    fontStyle: 'italic',
  },
};

// =============================================================================
// HELPERS
// =============================================================================

const gradePillColor = (grade) => {
  if (grade === 'Grade 7') return '#00ACC1';
  if (grade === 'Grade 8') return '#E65100';
  if (grade === 'Grade 9') return '#3F51B5';
  if (grade === 'Grade 10') return '#7B1FA2';
  if (grade === 'Grade 11') return '#00838F';
  if (grade === 'Grade 12') return KA_COLORS.red;
  return '#888';
};

// CSV download helper — converts a 2D array (rows of cells) into a CSV string
// and triggers a browser download.
const downloadCsv = (filename, rows) => {
  const escape = (v) => {
    if (v == null) return '';
    const s = String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const csv = rows.map((r) => r.map(escape).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Filter sections to those relevant to a given scope. For MS, only sections
// whose appliesTo includes 'ms' (library, athletics, finance, ms_final).
// For US, sections that include any of '9-11', 'senior', 'international'.
// For all, every section.
const sectionsForScope = (scopeId) => {
  if (scopeId === 'ms') {
    return CHECKOUT_SECTIONS.filter((s) => s.appliesTo?.includes('ms'));
  }
  if (scopeId === 'us') {
    return CHECKOUT_SECTIONS.filter(
      (s) =>
        s.appliesTo?.includes('9-11') ||
        s.appliesTo?.includes('senior') ||
        s.appliesTo?.includes('international'),
    );
  }
  return CHECKOUT_SECTIONS;
};

// =============================================================================
// REPORT DATA BUILDERS
//
// Pure functions that turn (records, scopeId) into structured rows for a
// given report. Used by BOTH the on-screen components (via useMemo) and the
// PDF builder. Keeping them outside the components ensures the printed PDF
// matches exactly what's on screen.
// =============================================================================

const buildSectionBottlenecksRows = (records, scopeId) => {
  const sections = sectionsForScope(scopeId);
  return sections.map((section) => {
    let pending = 0;
    let signed = 0;
    let na = 0;
    records.forEach((r) => {
      const status = r.sections?.[section.id]?.status;
      if (status === SECTION_STATUS.PENDING) pending += 1;
      else if (status === SECTION_STATUS.SIGNED) signed += 1;
      else na += 1;
    });
    const totalRelevant = pending + signed;
    const pctSigned = totalRelevant === 0 ? 0 : Math.round((signed / totalRelevant) * 100);
    return {
      id: section.id,
      label: section.label,
      pending,
      signed,
      na,
      totalRelevant,
      pctSigned,
    };
  });
};

const buildCompletionBucketsRows = (records) => {
  const defs = [
    { label: '0%', test: (p) => p === 0 },
    { label: '1–25%', test: (p) => p > 0 && p <= 25 },
    { label: '26–50%', test: (p) => p > 25 && p <= 50 },
    { label: '51–75%', test: (p) => p > 50 && p <= 75 },
    { label: '76–99%', test: (p) => p > 75 && p < 100 },
    { label: '100%', test: (p) => p === 100 },
  ];
  return defs.map((b) => {
    const count = records.filter((r) =>
      b.test(r.completionPercent ?? computeCompletionPercent(r)),
    ).length;
    return { label: b.label, count };
  });
};

const buildStudentListRows = (records, mode) => {
  let filtered = records.map((r) => ({
    ...r,
    _pct: r.completionPercent ?? computeCompletionPercent(r),
  }));
  if (mode === 'stuck') {
    filtered = filtered.filter((r) => r._pct < 100);
    filtered.sort((a, b) => a._pct - b._pct || a.studentName.localeCompare(b.studentName));
    filtered = filtered.slice(0, 50);
  } else if (mode === 'almost_done') {
    filtered = filtered.filter((r) => r._pct >= 80 && r._pct < 100);
    filtered.sort((a, b) => b._pct - a._pct || a.studentName.localeCompare(b.studentName));
  }
  return filtered.map((r) => {
    const pendingSectionIds = (getRelevantSectionIds(r) || []).filter(
      (sid) => r.sections?.[sid]?.status === SECTION_STATUS.PENDING,
    );
    const pendingLabels = pendingSectionIds
      .map((sid) => CHECKOUT_SECTIONS.find((s) => s.id === sid)?.label || sid)
      .join(', ');
    return { ...r, pendingSectionIds, pendingLabels };
  });
};

// =============================================================================
// PDF BUILDER
//
// KA-branded multi-page PDF generator. Style matches the AiChartGenerator /
// Peer Benchmarking PDFs: navy/gold cover, navy header bar on each report
// page, "Internal use only" footer.
//
// Usage:
//   const doc = newPdfDoc();
//   addPdfCover(doc, 'Section Bottlenecks', '...subtitle...');
//   addReportToPdf(doc, 'section_bottlenecks', scope, scopedRecords);
//   addReportToPdf(doc, 'section_bottlenecks', scope2, scopedRecords2);
//   doc.save(`...pdf`);
// =============================================================================

// Convert hex to [r,g,b] tuple for jsPDF.
const hexToRgb = (hex) => {
  const m = hex.replace('#', '').match(/.{2}/g);
  return m.map((h) => parseInt(h, 16));
};

const PDF_COLORS = {
  navy: hexToRgb(KA_COLORS.navy),       // 35,60,95
  gold: hexToRgb(KA_COLORS.gold),       // 162,145,100
  red: hexToRgb(KA_COLORS.red),         // 232,53,48
  ink: [26, 23, 20],
  muted: [122, 112, 104],
  white: [255, 255, 255],
  goldLight: [212, 172, 106],
  cream: [250, 247, 242],
  paleGold: [200, 191, 176],
  bgLight: [245, 246, 248],
};

const newPdfDoc = () =>
  new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

// Cover page. Heavy navy fill, gold rule, centered title block.
const addPdfCover = (doc, title, subtitle) => {
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const { navy, gold, goldLight, paleGold, muted, cream } = PDF_COLORS;
  const now = new Date().toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  doc.setFillColor(...navy);
  doc.rect(0, 0, W, H, 'F');
  doc.setFillColor(...gold);
  doc.rect(20, H / 2 - 2, W - 40, 1.5, 'F');

  doc.setTextColor(...goldLight);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text("KING'S ACADEMY  ·  INTERNAL REFERENCE", W / 2, H / 2 - 32, { align: 'center' });

  doc.setTextColor(...cream);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text(title, W / 2, H / 2 - 16, { align: 'center' });

  if (subtitle) {
    doc.setFontSize(13);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(...paleGold);
    doc.text(subtitle, W / 2, H / 2 - 6, { align: 'center' });
  }

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...muted);
  doc.text(`Checkout Report · ${now}`, W / 2, H / 2 + 14, { align: 'center' });
};

// Footer added after the doc is fully built — paginates all non-cover pages.
const addPdfFooters = (doc) => {
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const { cream, muted } = PDF_COLORS;
  const pc = doc.internal.getNumberOfPages();
  for (let i = 2; i <= pc; i += 1) {
    doc.setPage(i);
    doc.setFillColor(...cream);
    doc.rect(0, H - 10, W, 10, 'F');
    doc.setFontSize(7);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(...muted);
    doc.text('Internal use only · King\'s Academy Checkout Reports', W / 2, H - 4, { align: 'center' });
    doc.text(`${i - 1} / ${pc - 1}`, W - 14, H - 4);
  }
};

// Header band for a report page. Navy bar with title and subtitle on right.
const drawReportHeader = (doc, title, subtitle) => {
  const W = doc.internal.pageSize.getWidth();
  const { navy, gold, cream, goldLight } = PDF_COLORS;
  doc.setFillColor(...navy);
  doc.rect(0, 0, W, 24, 'F');
  doc.setFillColor(...gold);
  doc.rect(0, 24, W, 0.8, 'F');
  doc.setTextColor(...cream);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(title, 14, 15);
  if (subtitle) {
    doc.setTextColor(...goldLight);
    doc.setFontSize(8);
    doc.text(subtitle, W - 14, 15, { align: 'right' });
  }
};

// Render one (reportId, scope) section into the doc. Adds a new page first.
const addReportToPdf = (doc, reportId, scope, allRecords) => {
  const scopedRecords = allRecords.filter(scope.filter);
  const report = REPORTS.find((r) => r.id === reportId);
  if (!report) return;

  doc.addPage();
  const subtitle = `${scope.label} · ${scopedRecords.length} student${scopedRecords.length === 1 ? '' : 's'}`;
  drawReportHeader(doc, report.label, subtitle);

  const { navy, ink, muted, bgLight } = PDF_COLORS;
  const startY = 34;

  // Description text above the table
  doc.setTextColor(...muted);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'italic');
  doc.text(report.description, 14, startY);

  // Empty state
  if (scopedRecords.length === 0) {
    doc.setTextColor(...muted);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'italic');
    doc.text('No records in this scope.', 14, startY + 12);
    return;
  }

  // Common autotable styles
  const baseStyles = {
    font: 'helvetica',
    fontSize: 9,
    cellPadding: 3,
    textColor: ink,
    lineColor: [220, 220, 225],
    lineWidth: 0.1,
  };
  const headStyles = {
    fillColor: navy,
    textColor: PDF_COLORS.cream,
    fontStyle: 'bold',
    fontSize: 9,
    halign: 'left',
  };
  const alternateRowStyles = { fillColor: bgLight };

  if (reportId === 'section_bottlenecks') {
    const rows = buildSectionBottlenecksRows(scopedRecords, scope.id);
    autoTable(doc, {
      startY: startY + 6,
      head: [['Section', 'Pending', 'Signed', 'N/A', '% Signed']],
      body: rows.map((r) => [
        r.label,
        r.pending,
        r.signed,
        r.na,
        `${r.pctSigned}%`,
      ]),
      styles: baseStyles,
      headStyles,
      alternateRowStyles,
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { halign: 'right', cellWidth: 22 },
        2: { halign: 'right', cellWidth: 22 },
        3: { halign: 'right', cellWidth: 22 },
        4: { halign: 'right', cellWidth: 26 },
      },
      didParseCell: (data) => {
        // Color the numeric cells: pending in dark amber, signed in green
        if (data.section === 'body') {
          if (data.column.index === 1 && data.cell.raw > 0) {
            data.cell.styles.textColor = [178, 106, 0];
            data.cell.styles.fontStyle = 'bold';
          } else if (data.column.index === 2 && data.cell.raw > 0) {
            data.cell.styles.textColor = [30, 122, 58];
            data.cell.styles.fontStyle = 'bold';
          } else if (data.column.index === 3) {
            data.cell.styles.textColor = muted;
          }
        }
      },
    });
  } else if (reportId === 'completion_buckets') {
    const rows = buildCompletionBucketsRows(scopedRecords);
    const total = scopedRecords.length;
    autoTable(doc, {
      startY: startY + 6,
      head: [['Completion', 'Count', '% of Total']],
      body: rows.map((r) => [
        r.label,
        r.count,
        total === 0 ? '0%' : `${Math.round((r.count / total) * 100)}%`,
      ]),
      styles: baseStyles,
      headStyles,
      alternateRowStyles,
      columnStyles: {
        0: { fontStyle: 'bold' },
        1: { halign: 'right' },
        2: { halign: 'right', textColor: muted },
      },
    });
  } else if (reportId === 'stuck_students' || reportId === 'almost_done') {
    const mode = reportId === 'stuck_students' ? 'stuck' : 'almost_done';
    const rows = buildStudentListRows(scopedRecords, mode);
    if (rows.length === 0) {
      doc.setTextColor(...muted);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'italic');
      doc.text(
        mode === 'stuck'
          ? 'No students remaining — everyone in this scope is fully signed off.'
          : 'No students in the 80–99% range right now.',
        14,
        startY + 14,
      );
      return;
    }
    autoTable(doc, {
      startY: startY + 6,
      head: [['Student', 'Grade', 'Cohort', 'Completion', 'Pending Sections']],
      body: rows.map((r) => [
        r.studentName,
        r.grade || '—',
        r.cohort || '—',
        `${r._pct}%`,
        r.pendingLabels || '—',
      ]),
      styles: baseStyles,
      headStyles,
      alternateRowStyles,
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 45 },
        1: { cellWidth: 22 },
        2: { cellWidth: 22, textColor: muted },
        3: { halign: 'right', cellWidth: 22 },
        4: { cellWidth: 'auto', fontSize: 8, textColor: muted },
      },
    });
  }
};

// Date stamp used in filenames.
const todayStamp = () => new Date().toISOString().slice(0, 10);

// =============================================================================
// REPORT BODIES
// =============================================================================

const SectionBottlenecksBody = ({ records, scopeId, scopeLabel }) => {
  const sections = sectionsForScope(scopeId);

  const rows = useMemo(() => {
    return sections.map((section) => {
      let pending = 0;
      let signed = 0;
      let na = 0;
      records.forEach((r) => {
        const status = r.sections?.[section.id]?.status;
        if (status === SECTION_STATUS.PENDING) pending += 1;
        else if (status === SECTION_STATUS.SIGNED) signed += 1;
        else na += 1; // includes missing
      });
      const totalRelevant = pending + signed;
      const pctSigned = totalRelevant === 0 ? 0 : Math.round((signed / totalRelevant) * 100);
      return {
        id: section.id,
        label: section.label,
        pending,
        signed,
        na,
        totalRelevant,
        pctSigned,
      };
    });
  }, [records, sections]);

  const handleCsv = () => {
    const csvRows = [
      ['Section', 'Pending', 'Signed', 'N/A', 'Total Relevant', '% Signed'],
      ...rows.map((r) => [r.label, r.pending, r.signed, r.na, r.totalRelevant, `${r.pctSigned}%`]),
    ];
    downloadCsv(
      `checkout-bottlenecks-${scopeId}-${todayStamp()}.csv`,
      csvRows,
    );
  };

  const handlePdf = () => {
    const doc = newPdfDoc();
    addPdfCover(doc, 'Section Bottlenecks', scopeLabel);
    const scope = SCOPES.find((s) => s.id === scopeId);
    addReportToPdf(doc, 'section_bottlenecks', scope, records);
    addPdfFooters(doc);
    doc.save(`checkout-bottlenecks-${scopeId}-${todayStamp()}.pdf`);
  };

  if (records.length === 0) {
    return <div style={styles.emptyMessage}>No records in this scope.</div>;
  }

  return (
    <>
      <div style={styles.cardHeader}>
        <div>
          <div style={styles.cardTitle}>{scopeLabel}</div>
          <div style={styles.cardCount}>{records.length} student{records.length === 1 ? '' : 's'}</div>
        </div>
        <div style={styles.cardActions}>
          <button style={styles.csvButton} onClick={handleCsv}>CSV</button>
          <button style={styles.pdfButton} onClick={handlePdf}>PDF</button>
        </div>
      </div>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Section</th>
            <th style={{ ...styles.th, ...styles.thRight }}>Pending</th>
            <th style={{ ...styles.th, ...styles.thRight }}>Signed</th>
            <th style={{ ...styles.th, ...styles.thRight }}>N/A</th>
            <th style={{ ...styles.th, ...styles.thRight }}>% Signed</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td style={styles.td}>{r.label}</td>
              <td style={{ ...styles.td, ...styles.tdRight, color: r.pending > 0 ? '#B26A00' : '#888', fontWeight: r.pending > 0 ? 600 : 400 }}>
                {r.pending}
              </td>
              <td style={{ ...styles.td, ...styles.tdRight, color: '#1e7a3a', fontWeight: r.signed > 0 ? 600 : 400 }}>
                {r.signed}
              </td>
              <td style={{ ...styles.td, ...styles.tdRight, ...styles.tdMuted }}>{r.na}</td>
              <td style={{ ...styles.td, ...styles.tdRight }}>
                <span style={styles.bar}>
                  <span style={styles.barFill(r.pctSigned, '#4caf50')} />
                </span>
                {r.pctSigned}%
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

const CompletionBucketsBody = ({ records, scopeId, scopeLabel }) => {
  const buckets = useMemo(() => {
    const defs = [
      { label: '0%', test: (p) => p === 0 },
      { label: '1–25%', test: (p) => p > 0 && p <= 25 },
      { label: '26–50%', test: (p) => p > 25 && p <= 50 },
      { label: '51–75%', test: (p) => p > 50 && p <= 75 },
      { label: '76–99%', test: (p) => p > 75 && p < 100 },
      { label: '100%', test: (p) => p === 100 },
    ];
    return defs.map((b) => {
      const count = records.filter((r) => b.test(r.completionPercent ?? computeCompletionPercent(r))).length;
      return { label: b.label, count };
    });
  }, [records]);

  const total = records.length;
  const maxCount = Math.max(1, ...buckets.map((b) => b.count));

  const handleCsv = () => {
    downloadCsv(
      `checkout-buckets-${scopeId}-${todayStamp()}.csv`,
      [
        ['Bucket', 'Count', '% of Total'],
        ...buckets.map((b) => [
          b.label,
          b.count,
          total === 0 ? '0%' : `${Math.round((b.count / total) * 100)}%`,
        ]),
      ],
    );
  };

  const handlePdf = () => {
    const doc = newPdfDoc();
    addPdfCover(doc, 'Completion Buckets', scopeLabel);
    const scope = SCOPES.find((s) => s.id === scopeId);
    addReportToPdf(doc, 'completion_buckets', scope, records);
    addPdfFooters(doc);
    doc.save(`checkout-buckets-${scopeId}-${todayStamp()}.pdf`);
  };

  if (total === 0) {
    return <div style={styles.emptyMessage}>No records in this scope.</div>;
  }

  return (
    <>
      <div style={styles.cardHeader}>
        <div>
          <div style={styles.cardTitle}>{scopeLabel}</div>
          <div style={styles.cardCount}>{total} student{total === 1 ? '' : 's'}</div>
        </div>
        <div style={styles.cardActions}>
          <button style={styles.csvButton} onClick={handleCsv}>CSV</button>
          <button style={styles.pdfButton} onClick={handlePdf}>PDF</button>
        </div>
      </div>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Completion</th>
            <th style={{ ...styles.th, ...styles.thRight }}>Count</th>
            <th style={{ ...styles.th, ...styles.thRight }}>% of Total</th>
            <th style={styles.th}>Distribution</th>
          </tr>
        </thead>
        <tbody>
          {buckets.map((b) => {
            const pctOfTotal = total === 0 ? 0 : Math.round((b.count / total) * 100);
            const barPct = (b.count / maxCount) * 100;
            const isComplete = b.label === '100%';
            return (
              <tr key={b.label}>
                <td style={{ ...styles.td, fontWeight: 600 }}>{b.label}</td>
                <td style={{ ...styles.td, ...styles.tdRight }}>{b.count}</td>
                <td style={{ ...styles.td, ...styles.tdRight, ...styles.tdMuted }}>{pctOfTotal}%</td>
                <td style={styles.td}>
                  <span style={{ ...styles.bar, width: 160 }}>
                    <span style={styles.barFill(barPct, isComplete ? '#4caf50' : KA_COLORS.gold)} />
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
};

const StudentListBody = ({ records, scopeId, scopeLabel, mode }) => {
  // mode: 'stuck' (ascending, lowest first, exclude 100%)
  //       'almost_done' (>=80%, <100%, ascending so easier wins first)
  const rows = useMemo(() => {
    let filtered = records.map((r) => ({
      ...r,
      _pct: r.completionPercent ?? computeCompletionPercent(r),
    }));
    if (mode === 'stuck') {
      filtered = filtered.filter((r) => r._pct < 100);
      filtered.sort((a, b) => a._pct - b._pct || a.studentName.localeCompare(b.studentName));
      filtered = filtered.slice(0, 50);
    } else if (mode === 'almost_done') {
      filtered = filtered.filter((r) => r._pct >= 80 && r._pct < 100);
      filtered.sort((a, b) => b._pct - a._pct || a.studentName.localeCompare(b.studentName));
    }
    return filtered.map((r) => {
      const pendingSectionIds = (getRelevantSectionIds(r) || []).filter(
        (sid) => r.sections?.[sid]?.status === SECTION_STATUS.PENDING,
      );
      const pendingLabels = pendingSectionIds
        .map((sid) => CHECKOUT_SECTIONS.find((s) => s.id === sid)?.label || sid)
        .join(', ');
      return { ...r, pendingSectionIds, pendingLabels };
    });
  }, [records, mode]);

  const handleCsv = () => {
    const csvRows = [
      ['Student', 'Grade', 'Cohort', 'Completion %', 'Pending Sections'],
      ...rows.map((r) => [
        r.studentName,
        r.grade,
        r.cohort,
        `${r._pct}%`,
        r.pendingLabels,
      ]),
    ];
    const filenameBase = mode === 'stuck' ? 'checkout-stuck' : 'checkout-almost-done';
    downloadCsv(
      `${filenameBase}-${scopeId}-${todayStamp()}.csv`,
      csvRows,
    );
  };

  const handlePdf = () => {
    const doc = newPdfDoc();
    const reportId = mode === 'stuck' ? 'stuck_students' : 'almost_done';
    const reportLabel = mode === 'stuck' ? 'Stuck Students' : 'Almost Done';
    const filenameBase = mode === 'stuck' ? 'checkout-stuck' : 'checkout-almost-done';
    addPdfCover(doc, reportLabel, scopeLabel);
    const scope = SCOPES.find((s) => s.id === scopeId);
    addReportToPdf(doc, reportId, scope, records);
    addPdfFooters(doc);
    doc.save(`${filenameBase}-${scopeId}-${todayStamp()}.pdf`);
  };

  return (
    <>
      <div style={styles.cardHeader}>
        <div>
          <div style={styles.cardTitle}>{scopeLabel}</div>
          <div style={styles.cardCount}>
            {rows.length} student{rows.length === 1 ? '' : 's'}
            {mode === 'stuck' && rows.length === 50 && ' (showing top 50)'}
          </div>
        </div>
        <div style={styles.cardActions}>
          <button style={styles.csvButton} onClick={handleCsv}>CSV</button>
          <button style={styles.pdfButton} onClick={handlePdf}>PDF</button>
        </div>
      </div>
      {rows.length === 0 ? (
        <div style={styles.emptyMessage}>
          {mode === 'stuck'
            ? 'No students remaining — everyone in this scope is fully signed off.'
            : 'No students in the 80–99% range right now.'}
        </div>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Student</th>
              <th style={styles.th}>Grade</th>
              <th style={styles.th}>Cohort</th>
              <th style={{ ...styles.th, ...styles.thRight }}>Completion</th>
              <th style={styles.th}>Pending Sections</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id || r.studentId}>
                <td style={{ ...styles.td, fontWeight: 600 }}>{r.studentName}</td>
                <td style={styles.td}>
                  <span style={{ ...styles.gradePill, background: gradePillColor(r.grade) }}>
                    {r.grade}
                  </span>
                </td>
                <td style={{ ...styles.td, ...styles.tdMuted }}>{r.cohort || '—'}</td>
                <td style={{ ...styles.td, ...styles.tdRight }}>
                  <span style={styles.bar}>
                    <span
                      style={styles.barFill(
                        r._pct,
                        r._pct >= 80 ? '#4caf50' : r._pct >= 50 ? KA_COLORS.gold : '#d9534f',
                      )}
                    />
                  </span>
                  {r._pct}%
                </td>
                <td style={{ ...styles.td, ...styles.tdMuted, fontSize: 12 }}>
                  {r.pendingLabels || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
};

// =============================================================================
// CARD WRAPPER — wraps a report body, one per scope
// =============================================================================

const ReportCard = ({ scope, records, reportId }) => {
  const scopedRecords = useMemo(
    () => records.filter(scope.filter),
    [records, scope],
  );

  let body;
  if (reportId === 'section_bottlenecks') {
    body = <SectionBottlenecksBody records={scopedRecords} scopeId={scope.id} scopeLabel={scope.label} />;
  } else if (reportId === 'completion_buckets') {
    body = <CompletionBucketsBody records={scopedRecords} scopeId={scope.id} scopeLabel={scope.label} />;
  } else if (reportId === 'stuck_students') {
    body = <StudentListBody records={scopedRecords} scopeId={scope.id} scopeLabel={scope.label} mode="stuck" />;
  } else if (reportId === 'almost_done') {
    body = <StudentListBody records={scopedRecords} scopeId={scope.id} scopeLabel={scope.label} mode="almost_done" />;
  }

  return <div style={styles.card}>{body}</div>;
};

// =============================================================================
// MAIN PAGE
// =============================================================================

export default function CheckoutReports() {
  const { user } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeReportId, setActiveReportId] = useState('section_bottlenecks');

  // Live subscription on /checkoutRecords — current school year, non-archived.
  // Matches the pattern used by StudentCheckout. School year is hard-coded to
  // DEFAULT_SCHOOL_YEAR for now (matches existing pattern); change to read
  // /config/systemSettings.currentSchoolYear if you want it dynamic.
  useEffect(() => {
    if (!user) return undefined;
    const q = query(
      collection(db, 'checkoutRecords'),
      where('schoolYear', '==', DEFAULT_SCHOOL_YEAR),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        // Filter out archived in-memory rather than in the query (avoids
        // needing a composite index).
        const active = all.filter((r) => r.status !== RECORD_STATUS.ARCHIVED);
        setRecords(active);
        setLoading(false);
      },
      (err) => {
        console.error('CheckoutReports subscription error:', err);
        setLoading(false);
      },
    );
    return unsub;
  }, [user]);

  const activeReport = REPORTS.find((r) => r.id === activeReportId) || REPORTS[0];

  // Build a PDF of the active report across all 3 scopes.
  const downloadActiveReportPdf = () => {
    const doc = newPdfDoc();
    addPdfCover(doc, activeReport.label, 'All Scopes');
    SCOPES.forEach((scope) => {
      addReportToPdf(doc, activeReport.id, scope, records);
    });
    addPdfFooters(doc);
    doc.save(`checkout-${activeReport.id.replace(/_/g, '-')}-${todayStamp()}.pdf`);
  };

  // Build a single PDF of every report × every scope.
  const downloadEverythingPdf = () => {
    const doc = newPdfDoc();
    addPdfCover(doc, 'Checkout Reports', 'Complete Report Set');
    REPORTS.forEach((report) => {
      SCOPES.forEach((scope) => {
        addReportToPdf(doc, report.id, scope, records);
      });
    });
    addPdfFooters(doc);
    doc.save(`checkout-all-reports-${todayStamp()}.pdf`);
  };

  return (
    <div style={styles.outer}>
      <aside style={styles.sidebar} className="checkout-reports-sidebar">
        <div style={styles.sidebarHeader}>Reports</div>
        {REPORTS.map((r) => {
          const isActive = r.id === activeReportId;
          return (
            <button
              key={r.id}
              type="button"
              onClick={() => setActiveReportId(r.id)}
              style={{
                ...styles.sidebarItem,
                ...(isActive ? styles.sidebarItemActive : {}),
              }}
            >
              {r.label}
            </button>
          );
        })}
        <button
          type="button"
          onClick={downloadEverythingPdf}
          disabled={loading || records.length === 0}
          style={{
            ...styles.pdfEverythingButton,
            opacity: loading || records.length === 0 ? 0.5 : 1,
            cursor: loading || records.length === 0 ? 'not-allowed' : 'pointer',
          }}
          title="One PDF containing all 4 reports across all 3 scopes"
        >
          ⬇ Download Everything
        </button>
      </aside>

      <main style={styles.main} className="checkout-reports-main">
        <div style={styles.pageHeader}>
          <div>
            <h1 style={styles.pageTitle}>{activeReport.label}</h1>
            <div style={styles.pageDescription}>{activeReport.description}</div>
          </div>
          <button
            style={styles.pdfAllButton}
            onClick={downloadActiveReportPdf}
            disabled={loading || records.length === 0}
          >
            ⬇ Download PDF (this report)
          </button>
        </div>

        {loading ? (
          <div style={styles.loading}>Loading records…</div>
        ) : (
          SCOPES.map((scope) => (
            <ReportCard
              key={scope.id}
              scope={scope}
              records={records}
              reportId={activeReportId}
            />
          ))
        )}
      </main>
    </div>
  );
}